/**
 * The card chrome every answer shape is drawn on.
 *
 * ONE OBJECT PER ANSWER, RULED RATHER THAN SPACED. Nine shapes were loose
 * stacks separated by an 8px gap with the freshness stamp hanging under the
 * last of them as a tenth unrelated line. The artifact embed.css names -- a
 * broker's contract note -- separates its regions with a hairline inside one
 * bordered block, and that is what makes a figure, the thing it qualifies and
 * the stamp saying how old it is read as one statement rather than three.
 *
 * NOTHING HERE DECIDES WHETHER ANYTHING MAY BE DRAWN. `may_draw` in
 * tools/registry.py settled that server-side before the frame left.
 *
 * THE PADDING IS ON THE SECTIONS, NOT ON THE CARD. Every shape's own
 * hairlines are its grid and have to reach the border, so no shape branch is
 * wrapped in `Pad` -- see `Body`. `Pad` survives for the two regions that
 * genuinely are inset: the notes rail and the breakdown's checkable
 * sentences.
 */
import { Stamp } from '@/features/mitra/panel/Stamp'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { copy, type Lang } from '@/features/mitra/lib/copy'
import { useOpenPath, useRoutable } from '@/features/mitra/lib/openPath'
import {
  cellDirection,
  cellInferred,
  groupText,
  humanLabel,
  unitPrefix,
  unitSuffix,
  type CellValue,
} from '@/features/mitra/lib/figures'
import type { DataState } from '@/features/mitra/api/frames'
import type { Labelled } from '@/features/mitra/lib/project'

/**
 * The bordered, hairline-divided container. Every child is a region.
 *
 * `bg-surface` and not `bg-paper`, and the two are no longer the same colour
 * in either theme. They were both #FFFFFF in light, so a card separated by
 * its hairline alone; the redesign puts a step behind the card in both
 * halves -- #F6F7F9 under #FFFFFF, #000000 under #0E0F11 -- and the palette
 * reversal of 2026-09-07 took it. So the card lifts off its page by fill now,
 * and the hairline is the edge rather than the whole separation.
 *
 * `divide-rule` and not `divide-edge`. --color-edge is argued up to 3:1 in
 * embed.css because a control outline and a bar's track are graphical objects
 * a reader has to SEE, and its comment closes by handing separators back to
 * --color-rule. An edge-coloured divider inside a rule-coloured border draws
 * the inside of the card darker than its own outline.
 *
 * `overflow-hidden` clips the Foot's wash to the radius. It does not stop a
 * descendant `overflow-x-auto` from scrolling -- which is what `Wide` and the
 * table's scroller depend on -- but it DOES clip a nowrap figure that has no
 * scroller, which is why `Wide` exists.
 *
 * `data-role="card"` sits before the spread so `Projection` can override it.
 */
export function Card({
  children,
  className = '',
  ...rest
}: React.ComponentPropsWithoutRef<'section'> & { children: React.ReactNode }) {
  return (
    <section
      data-role="card"
      {...rest}
      className={`divide-y divide-rule overflow-hidden rounded-block
                  border border-rule bg-surface shadow-block ${className}`}
    >
      {children}
    </section>
  )
}

/**
 * The inset a card's genuinely-inset regions share.
 *
 * Two callers, deliberately: the notes rail and the breakdown's `rest`. Every
 * other region draws its own hairlines and pads itself to px-3, because a
 * rule that stops short of the border it is drawn against reads as a table
 * floating inside a card rather than as the card's own region.
 */
export function Pad({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return <div className={`px-3 py-2 ${className}`}>{children}</div>
}

/**
 * A box that scrolls sideways, and only sideways.
 *
 * `overflow-y-hidden` IS LOAD-BEARING, not tidying. CSS computes the other
 * axis to `auto` the moment one is not `visible`, so `overflow-x` alone makes
 * a box a scroll container on BOTH axes -- and a 28px headline at
 * `leading-none` overflows its own line box by a hair, which drew a VERTICAL
 * scrollbar beside the price. The horizontal scroll is the feature (a figure
 * does not wrap, so it must scroll rather than clip); the vertical one was
 * never asked for.
 *
 * A CONSTANT AND NOT A COMMENT, because the comment was already here, on
 * `Wide`, and the table's own scroller was written without having read it: the
 * same scrollbar shipped twice, once beside a price and once beside a table. A
 * rule a renderer has to have READ is a rule the next renderer breaks.
 */
export const SCROLL_X = 'overflow-x-auto overflow-y-hidden'

/**
 * Sub-pixel tolerance. A `w-full` table lands on its container's edge a
 * fraction of a pixel out, and without this every table that fits would be
 * judged too wide for the panel it exactly fills.
 *
 * Exported because the same tolerance answers the same question about a
 * clamped cell's HEIGHT in `Table` -- a box and its content that agree still
 * round apart by a pixel -- and two spellings of one pixel is how they come
 * to disagree.
 */
export const BOUNDARY_SLACK_PX = 1

/**
 * How much room the panel has to GAIN before a ledger goes back to being a
 * grid, and every pixel of it is a guard against one measurement.
 *
 * A single threshold is a knife edge, and this file has already paid for one:
 * the fold this replaced went missing on exactly two viewport widths in a
 * hundred because a column's right edge landed 0.22px past a scroller and the
 * arithmetic came out negative. A shape decision taken at a single width has
 * the same shape of failure, only louder -- a reader dragging the panel edge
 * across the boundary would watch the card turn itself inside out once per
 * pixel.
 *
 * SIXTEEN, AND THE NUMBER IS MEASURED RATHER THAN CHOSEN. It has to clear the
 * sub-pixel wobble `BOUNDARY_SLACK_PX` names, and it has to clear the couple
 * of pixels a column moves by when Inter and Hind swap in over the fallback
 * stack -- a Devanagari register loads a second face, so the swap moves more
 * here than it would in an English-only widget. It has to stay well under the
 * width of one column, because the band is room the panel HAS and the grid is
 * not being given: 16px is about two digits of the 13px mono face, and the
 * narrowest column on this surface is wider than that.
 */
const REGRID_MARGIN_PX = 16

/** Which of the two shapes a table's rows are drawn in. */
export type TableShape = 'grid' | 'ledger'

/**
 * Whether these rows are drawn as a grid or as a ledger of records.
 *
 * THIS IS THE FOURTH ANSWER TO ONE QUESTION AND THE FIRST THAT DOES NOT CUT.
 * The three before it all took the panel's width as fixed and the table's
 * shape as fixed, and argued about where to put the damage. `mask-image`
 * faded the last column of every table alike, because a paint cannot tell
 * whether the region it covers scrolls. A `local`/`scroll` background pair
 * does self-detect and paints BEHIND the digits it was meant to soften. A
 * scroll-position overlay fades a partial number rather than removing it,
 * which is the failure in a softer colour.
 *
 * The fourth was a measured fold: widen the last column that fits so the cut
 * lands BETWEEN two columns rather than inside one, and mark it so a reader
 * knows to pan. That was right about the failure -- "38,038." against a card
 * edge is a figure, not a fold, and a truncated figure reads as complete --
 * and it settled for the wrong remedy. It left a reader panning sideways to
 * read one dividend row, and it left a company name clipped to "...ncy..."
 * in a column too narrow for it, which is the report this replaced it on:
 * "where scroll is not needed and all info is present in card table".
 *
 * SO THE MEASUREMENT DECIDES THE SHAPE INSTEAD OF THE CUT. Hand this the
 * width the table's own content wants and the width it has; where the columns
 * fit, the grid is the right drawing and is returned unchanged -- it is what
 * makes twenty-five rows comparable at a glance, and nothing here may cost it
 * that. Where they do not, a grid is the wrong drawing at any cut, and the
 * rows are redrawn as records: each row's identity whole on its own line and
 * the remaining columns as label-value pairs beneath it. No cut, no
 * scrollbar, no truncated identifier, and the one axis the panel actually has
 * to spare is the one it grows along.
 *
 * TWO THRESHOLDS AND NOT ONE, which is what `drawing` is for. The grid gives
 * way as soon as it overflows, because an overflowing grid is the defect. The
 * ledger gives way only once the panel has `REGRID_MARGIN_PX` of room to
 * spare, because a ledger that flips back at the same width it flipped at
 * would flip again on the next repaint.
 *
 * `natural` IS REMEMBERED BY THE CALLER AND NOT MEASURED IN LEDGER MODE,
 * because there is no grid in the DOM to measure. That is sound in the one
 * direction it has to be: the rows only ever GROW while a card is open -- the
 * `show all` control adds rows and nothing removes them -- and more rows can
 * only want more width, so a remembered natural width can be too small and
 * never too large. Too small costs one extra pass through the grid at a width
 * where it turns out not to fit; too large would have stranded the reader in
 * a ledger at a width where the grid was the better drawing.
 */
export function tableShape(natural: number, available: number, drawing: TableShape): TableShape {
  if (drawing === 'ledger') return available >= natural + REGRID_MARGIN_PX ? 'grid' : 'ledger'
  return natural > available + BOUNDARY_SLACK_PX ? 'ledger' : 'grid'
}

/**
 * A figure too wide for its cell scrolls; it never clips.
 *
 * `Fig` is `whitespace-nowrap` and `Card` is `overflow-hidden`, so a total in
 * crores or a paid-up capital in rupees loses its last digits silently -- a
 * wrong figure, not a tight one, and one no viewport test can see because the
 * document does not scroll. `Table` has used the same trick since it was
 * written. Deliberately NOT `data-role="projection-scroll"`: that role names
 * the one box that stops a wide table panning the conversation, and two boxes
 * with one name make the table's guarantee untestable.
 */
export function Wide({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return <span className={`-mx-3 block ${SCROLL_X} px-3 ${className}`}>{children}</span>
}

/**
 * Who or what this card is about: a mark, a name, a qualifying line, and one
 * slot on the right for a destination.
 *
 * THE MICRO-LABEL HEADING IS GONE. A `RenderDirective.title` was set at 10px
 * uppercase with 0.08em tracking -- the same treatment the raw column key
 * `CHANGE_PCT` got, which is what made a caption somebody wrote look like an
 * identifier a machine emitted. A title is a name; it is set as one.
 *
 * THE BADGE IS AN IDENTIFIER, NOT A MONOGRAM. Nothing here derives initials
 * from a company name -- that would put a mark on screen the platform does
 * not have, and a wrong identifier is the likeliest wrong answer this product
 * can give. Callers pass a symbol or a sign, so the box grows rather than
 * clipping five characters into four.
 *
 * `min-w-0` on the name column and `truncate` on the meta are not polish. The
 * right-hand slot is empty in phase one and will carry a destination pill;
 * without both, adding that pill re-lays out every header on the surface
 * instead of taking space already reserved.
 */
export function EntityHead({
  badge,
  name,
  meta,
  action,
}: {
  badge?: React.ReactNode
  name: React.ReactNode
  meta?: React.ReactNode
  /** PHASE TWO. Reserved, rendered only when filled. */
  action?: React.ReactNode
}) {
  return (
    <div data-role="entity" className="flex items-center gap-2 bg-ground px-3 py-2">
      {badge != null && (
        <span
          data-role="entity-badge"
          // ROUND, WHICH IS WHAT THE PROTOTYPE DRAWS. A rounded square beside
          // a 14px card and 999px pills was the last 6px corner on the
          // surface. It still GROWS rather than clipping -- a five-character
          // symbol clipped to four is a different symbol -- so a long one
          // relaxes into a stadium rather than being squeezed into a circle.
          // NO OUTLINE, and the theme sweep is what settled it: every
          // pill-shaped thing in this widget is drawn with --color-edge,
          // because an outlined control's boundary is the only thing saying
          // it is there. This is not a control -- it is a filled mark naming
          // the subject -- so it takes the fill and drops the border rather
          // than borrowing a control's weight.
          // Figma 34 -> h-8/min-w-8 (4-grid; non-interactive mark, nearest-8 tie-break)
          className="flex h-8 min-w-8 shrink-0 items-center justify-center
                     rounded-pill bg-ground px-2 font-mono
                     text-mono-s font-semibold tracking-[-0.02em] text-ink-mid"
        >
          {badge}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span data-role="entity-name" className="line-clamp-2 text-h-s leading-tight text-ink">
          {name}
        </span>
        {meta && (
          <span
            data-role="entity-meta"
            className="truncate text-label-s leading-snug text-ink-soft"
          >
            {meta}
          </span>
        )}
      </div>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  )
}

/**
 * The one style every figure on this surface is set in.
 *
 * Named for the reason `SYMBOL` is: the table's figure columns had it written
 * out beside `Fig`'s copy of it, and a treatment written twice is a treatment
 * that diverges. `text-right` is NOT in here -- alignment is a column's
 * decision and a card's figures are not in columns.
 */
export const FIGURE = 'whitespace-nowrap font-mono tabular-nums'

/**
 * A figure's digits, grouped, and the mark that says the engine derived it.
 *
 * THE HALF OF `Fig` THAT IS TRUE OF A FIGURE ANYWHERE. The other half is the
 * unit, and a table takes it once at the top rather than on every row:
 * `unitPrefix` argues a figure standing alone has to carry its own, and a
 * column is a repetition, so the heading is where a column's unit belongs.
 * THAT WAS AN ASSUMPTION ABOUT THE HEADING UNTIL 2026-09-10 and it was false
 * of most of them -- YOUR WEIGHT carried no unit and drew a bare `18.4` next
 * to a rupee figure. `columnAffix` in Table.tsx is what makes it true now, by
 * reading the unit off the column's own cells; a heading that states no unit
 * is no longer a licence for this to drop one. So
 * the `<td>` wrote this half out by hand and drifted from it -- one
 * `get_holdings` result drew 1,06,26,370 in the positions card and 10626370 in
 * the default table under it, and neither renderer's own assertions could see
 * a disagreement only the two together make visible.
 *
 * GROUPED, NEVER RE-FORMATTED. `grouped` splits the string the engine sent and
 * spaces its digits two-two-three, the way `calc/speech.py` counts; it parses
 * nothing, so the fraction and every digit past 2^53 arrive on screen exactly
 * as the wire wrote them. `Number.parseFloat("11.50")` prints "11.5", which
 * claims a dividend is known to fewer places than the engine said it was.
 *
 * A SUFFIX GOES INSIDE THE MARK AND NOT AFTER IT. The underline marks the
 * whole figure rather than its digits, so "-7.18%" is underlined entire and
 * never "-7.18" with a bare percent sign hanging outside the mark. The rule
 * predates the mark: it was written when this drew a dagger, where "-7.18†%"
 * would have been a footnote on a number that then acquired a unit.
 */
export function Digits({ cell, suffix = null }: { cell: CellValue; suffix?: React.ReactNode }) {
  return (
    <>
      {cellInferred(cell) ? (
        // A DOTTED UNDERLINE, NOT A DAGGER. Reported as "this + seems like
        // cross": `21.91†` set a footnote dagger in superscript beside a
        // number, and at that size above a figure a dagger reads as a plus or
        // a cross — either of which looks like part of the arithmetic. A
        // footnote dagger also assumes a footnote at the foot of a page, and
        // this surface has a card and a phone and no page.
        //
        // The underline is the other long-standing "there is a note about
        // this" convention and cannot be read as an operator: it sits UNDER
        // the digits instead of beside them, adds no character to the number
        // — so a reader copying the figure gets the figure — and cannot be
        // mistaken for a sign, an exponent or a unit.
        //
        // The card's own legend (`derived-key` in Projection.tsx) is what says
        // what it means, and it is real text rather than a hover, which is
        // what the `title` this replaced could never be on a phone.
        <span
          data-role="derived"
          className="underline decoration-dotted decoration-from-font
                         underline-offset-4"
        >
          {groupText(cell)}
          {suffix}
        </span>
      ) : (
        <>
          {groupText(cell)}
          {suffix}
        </>
      )}
    </>
  )
}

/**
 * A yes-or-no field, drawn as a mark rather than printed as the wire spells it.
 *
 * NO RAW BOOLEAN REACHES A READER. `cellText` renders `true` for a boolean and
 * returns a string untouched, and this engine sends that fact both ways from
 * one file -- `get_position` writes `{"held": True}` and `compare_peers` writes
 * `{"held": str(sym in holdings).lower()}` -- so a column headed HELD drew the
 * literal `true` down a card of rupee figures. That is a wire value on screen,
 * the same class of defect as the raw column key `columnLabel` exists to stop,
 * and it is why the caller decides this per COLUMN and covers both spellings.
 *
 * A MARK AND NOT A DROPPED COLUMN, which was the other way out. The column is
 * `_columns_of`'s own -- `columnsOf` in lib/project.ts mirrors that algorithm
 * exactly so this client cannot draw a different table from the engine's own
 * projection of the same envelope -- and a renderer that deletes a column
 * reintroduces precisely that drift, silently. `Comparison` settles it on
 * content too: the mark on the reader's own row is what turns six P/Es from
 * homework into a reading, so `held` is the most useful column on a peer
 * table rather than the least.
 *
 * THE GLYPH IS NEVER THE ONLY CHANNEL, which is `Delta`'s rule for its arrow
 * and Stamp.tsx's argument for refusing hues. The word rides along in
 * `sr-only`, and it is what keeps a known NO distinguishable from an unknown
 * for a reader who hears this table rather than sees it.
 *
 * A FALSE IS DRAWN AS NOTHING, and deliberately not as the em dash.
 * `cellText` spends that glyph on "the source does not have this", and a known
 * no is a different claim -- the same distinction that stops an unknown being
 * drawn as a zero. Unmarked-for-no is also `Comparison`'s own grammar: it
 * labels the held row and leaves every other alone.
 *
 * `relative` is load-bearing. `sr-only` is `position: absolute`, and
 * embed.css's base layer records what an absolute box with no positioned
 * ancestor does to this panel -- the document grows to reach it and the fixed
 * shell slides up. Two spans in Transcript.tsx were in that state.
 */
export function Mark({ yes }: { yes: boolean }) {
  return (
    <span
      data-role="mark"
      data-yes={yes ? 'true' : 'false'}
      className="relative inline-flex items-baseline text-ink-mid"
    >
      {yes && <span aria-hidden="true">✓</span>}
      <span className="sr-only">{yes ? MARK_YES : MARK_NO}</span>
    </span>
  )
}

// English, for `COLUMN_LABELS`' own reason one file over: these ride the
// `block` channel beside the platform's English sector names and page titles,
// and a translated mark under an untranslated heading reads worse than either
// alone. `copyYours` in shapes/Comparison.tsx is the same decision, made the
// same way.
const MARK_YES = 'yes'
const MARK_NO = 'no'

/**
 * A figure and its unit, in the one style every shape here writes a number in.
 *
 * NOWRAP LIVES HERE AND NOT ON THE BOX AROUND IT. The table put it on the
 * `<td>` and every other caller inherited nothing, so a figure inside a
 * `flex-wrap` row could break between "NPR" and the amount it qualifies.
 *
 * 0.72em and not the reference's 0.62em: one ratio serves a 21px headline and
 * an 11px extra on the same card, and 0.62em of 11px is 6.8px.
 */
export function Fig({
  cell,
  className = '',
  quietUnit = false,
}: {
  cell: CellValue
  className?: string
  /**
   * Set the unit smaller against a headline-sized figure.
   *
   * THE UNIT STAYS. Dropping it was tried and is wrong: `unitPrefix`
   * argues currency LEADS, and the deeper rule is that a number without
   * its unit is the defect `Figure` exists to prevent. An entity header
   * names the INSTRUMENT -- "NABIL · Commercial Banks" -- and never the
   * currency, so a bare headline loses the one fact the header cannot
   * supply. Two committed assertions say so and they are right.
   *
   * What was actually wrong is the RATIO. The affix is 0.72em, which is
   * comfortable at 13px and enormous at 28px: the unit was reading as
   * loud as the figure. Quiet is 0.42em, which lands near the card's own
   * small text whatever the headline grows to.
   */
  quietUnit?: boolean
}) {
  const before = unitPrefix(cell)
  const after = unitSuffix(cell)
  const unit = `${quietUnit ? 'text-[0.42em]' : 'text-[0.72em]'} font-normal text-ink-soft`
  return (
    <span data-role="fig" className={`${FIGURE} ${className}`}>
      {before && <span className={unit}>{before}</span>}
      <Digits cell={cell} suffix={after ? <span className={unit}>{after}</span> : null} />
    </span>
  )
}

/**
 * The micro-label this surface names things with, in the words' own case:
 * never set in capitals (owner's rule, 2026-10-31), which read as a
 * template's label rather than a name.
 */
export function Label({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return <span className={`text-mono-s font-semibold text-ink-soft ${className}`}>{children}</span>
}

/**
 * The one treatment a SYMBOL gets, wherever a shape leads with one.
 *
 * Named once because four shapes lead with an identifier -- a position, a
 * quote in a batch, a bars group like "NABIL / UPPER", a breakdown's subject
 * -- and the designs had it at three different sizes, weights and colours.
 * The reference's `.pos-sym`, and it is deliberately NOT the micro-label: a
 * symbol set at 10px uppercase with 0.08em tracking is the treatment this
 * surface gives a raw column key.
 */
export const SYMBOL = 'font-mono text-label-m font-semibold text-ink'

/**
 * What a figure did: the amount, which way, and by how much.
 *
 * DIRECTION READS THREE WAYS AT ONCE and none of them is a sign character.
 * `_measure()` sends `str(Decimal)`, so a rise arrives as "6.50" and not
 * "+6.50"; printing a plus would be typing a character the engine did not
 * send, which is the same rule that stops us re-formatting "11.50". So the
 * colour, the arrow and one spoken word carry it -- and the word is what
 * makes the claim survive a reader who cannot separate --color-up from
 * --color-down, which is the argument Stamp.tsx makes for refusing hues.
 *
 * THE ARROW LEADS WHEN THERE IS ONE FIGURE AND SITS BETWEEN WHEN THERE ARE
 * TWO. Every shape passes `value` alone today, because no payload names a
 * rupee change beside its percentage; with the reference's ordering that left
 * the glyph trailing nothing. Between two figures it goes back where the
 * reference puts it, which is the day `_positions()` names `change_amount`.
 *
 * FLAT TAKES NEITHER. Level is not a small amount of good news.
 *
 * `relative` is load-bearing. The `sr-only` span is `position: absolute`, and
 * embed.css's base layer records what an absolute box with no positioned
 * ancestor does to this panel: laid out against the initial containing block,
 * the document grows to reach it, and the fixed 100dvh shell slides up
 * leaving paper under the composer. Two spans in Transcript.tsx did exactly
 * that; this is the third such site and it is positioned from the start.
 */
export function Delta({
  value,
  percent,
  lang,
  className = '',
  ...rest
}: React.ComponentPropsWithoutRef<'span'> & {
  value: CellValue
  /** The percentage the amount represents, when the row carries both. */
  percent?: CellValue
  lang: Lang
}) {
  const words = copy(lang)
  // The percent when there is one: `change_pct` is what `_CHANGE_FIGURES`
  // names, so it is the figure whose sign the card is actually about.
  const direction = cellDirection(percent ?? value)
  const tone = direction === 'up' ? 'text-up' : direction === 'down' ? 'text-down' : 'text-ink-soft'
  const arrow =
    direction === 'flat' ? null : (
      <>
        <span aria-hidden="true" className="text-[0.9em] leading-none">
          {direction === 'up' ? '↗' : '↘'}
        </span>
        <span className="sr-only">{direction === 'up' ? words.deltaUp : words.deltaDown}</span>
      </>
    )
  /**
   * A GAIN CARRIES ITS SIGN. `Ideapreneur Nepal Brand Guidelines 2026` p.21,
   * under Data rules: "Use + / - signs and text for gains and losses." A loss
   * already arrives signed — the engine sends "-1.80" — and a gain arrives
   * bare, so only one half of the pair was ever readable without the colour.
   *
   * PREFIXED, NOT REFORMATTED, and the distinction is the one `grouped()`
   * makes one file over: a separator changes how digits are spaced and never
   * which digits they are, and a sign read off the value's OWN sign is the
   * same kind of move. What is deliberately NOT done is substituting a
   * typographic minus for the engine's hyphen — that would alter a character
   * the server sent, which is a different act from adding one it implied.
   *
   * `aria-hidden`, because the `sr-only` word above already says "up" and a
   * screen reader announcing "plus up zero point six zero percent" reads the
   * same fact twice.
   */
  const plus = direction === 'up' ? <span aria-hidden="true">+</span> : null
  return (
    <span
      data-role="delta"
      {...rest}
      data-direction={direction}
      className={`relative inline-flex items-baseline gap-1 whitespace-nowrap
                  font-mono text-body-s font-medium tabular-nums
                  ${tone} ${className}`}
    >
      {percent == null && arrow}
      {plus}
      <Fig cell={value} />
      {percent != null && arrow}
      {percent != null && plus}
      {percent != null && <Fig cell={percent} />}
    </span>
  )
}

/**
 * Ruled cells, with the outer hairlines clipped rather than suppressed.
 *
 * THE RULES ARE DRAWN BY OVERLAP AND NOT BY nth-child. The reference
 * suppresses the outer borders with `:nth-child(3n)` and
 * `:nth-last-child(-n+3)`, which is correct for exactly three columns and a
 * count that divides by three. Every grid on this surface is responsive and
 * every count is whatever a tool returned. So each cell draws its own left
 * and top rule and this wrapper clips the first row's and the first column's
 * -- the same picture, for any number of cells in any number of columns.
 *
 * Extracted because three shapes need it and each had written it out: a stats
 * strip, a quote's price row, a facts group. What they do NOT share is the
 * cell's contract, which is why `StatGrid` and `FactGrid` are two components
 * over one shell.
 */
// `ComponentProps` rather than `ComponentPropsWithoutRef`, so the one caller
// can measure the box it draws. `FactGrid` decides its own track from the
// width this element actually has, and the width a figure inside it actually
// wants -- neither of which any media query can see.
export function RuledGrid({
  track,
  children,
  ...rest
}: React.ComponentProps<'div'> & {
  track: string
  children: React.ReactNode
}) {
  return (
    <div {...rest} className="overflow-hidden">
      <div className={`-ml-px -mt-px grid ${track}`}>{children}</div>
    </div>
  )
}

/** One cell of a `RuledGrid`, drawing only the two rules the wrapper keeps. */
export function GridCell({
  children,
  ...rest
}: React.ComponentPropsWithoutRef<'div'> & { children: React.ReactNode }) {
  return (
    // 10px across, not 12, and the two pixels a side are what let the reply
    // column keep its 36px indent: a quote card is a two-column grid whose
    // figures do not wrap, so it was the one shape that could not give the
    // mark's gutter back out of its own width. Measured — a cell's own span
    // wanted 159px in a 155px box, exactly the 4px this returns.
    <div {...rest} className="min-w-0 border-l border-t border-rule px-2 py-2">
      {children}
    </div>
  )
}

/**
 * Named figures, each in its own ruled cell.
 *
 * TWO COLUMNS AT THE FLOOR. Measured at a 320px panel: the log's px-4 leaves
 * 286px of card, so three columns give 71px of text per cell and a
 * nine-character figure at 14px mono is about 76px. Two give 103px.
 * `min-[360px]:` is the breakpoint `Bars` already turns on for the same
 * reason. `columns={2}` is for a card whose figures carry NPR and lakh
 * scales -- `unitSuffix` renders a share count as a trailing " shares", so an
 * eight-digit NEPSE volume is about 109px and a three-column grid CLIPS it
 * rather than scrolling, which is a wrong figure.
 *
 * `humanLabel` runs here rather than at ten call sites. Its docstring calls
 * itself the last step before a label is drawn, and `unrealised_pct` reaching
 * a retail reader is the defect it exists to close. Deliberately English --
 * `COLUMN_LABELS` states the reason one screen away.
 *
 * THE RULE IS A GAP, NOT A BORDER, and that is the redesign's own strip
 * rather than a restyle of ours. It draws this exact shape:
 * `display:flex;gap:1px;background:var(--bd);border-radius:8px;
 * overflow:hidden` with each cell on `var(--sf)` at `padding:8px 10px`, its
 * label `400 8.5px 'JetBrains Mono'` at `.08em` in the quietest ink and its
 * value `700 12.5px 'JetBrains Mono'` tabular. So the hairline is the ground
 * showing through a one-pixel gap, the block has a corner of its own, and it
 * reads as a quiet panel inside the reply rather than as a ruled table beside
 * it. Reported as "why still old design?" against a quote card drawing four
 * bordered boxes, and the boxes were the whole of it.
 *
 * `grid` and not the reference's `flex`, because ITS cells hold "98.4 L
 * kitta" and OURS hold "1,06,26,370 shares" -- the server renders a figure
 * and this component may not abbreviate one. Three equal flex cells at 375px
 * give 118px against a measured 159px, so the strip keeps the design's
 * texture and takes its column count from the width instead: two at the
 * floor, three from 440px, which is where the quote card's own grid already
 * splits.
 */
export function StatGrid({ stats, columns = 3 }: { stats: readonly Labelled[]; columns?: 2 | 3 }) {
  const lang = useLabelLang()
  if (stats.length === 0) return null
  // ONE COLUMN AT THE FLOOR, AND THAT IS ABOUT SIDEWAYS SCROLL. `Fig` does
  // not wrap -- a figure is a figure at any width -- so a value wider than
  // its cell can only scroll or clip, and `Wide` chooses scroll because a
  // clipped figure reads as a complete one. Measured at 320px: a grouped
  // NEPSE volume renders 159px of "1,06,26,370 shares" into a 143px cell, so
  // the reader got a scrollbar inside a stat. Grouping is what pushed it
  // over -- three separators the bare digits did not have -- and the answer
  // is to give the cell the width, not to take the separators back.
  const track = columns === 2 ? 'grid-cols-1 w360:grid-cols-2' : 'grid-cols-2 w440:grid-cols-3'
  return (
    // `bg-rule` is the ground the one-pixel gaps show; `rounded-block` and
    // `overflow-hidden` give the strip the corner the reference draws. Nothing
    // here has a border, so two cells can never disagree about one.
    <div data-role="stats" className={`grid gap-px overflow-hidden rounded-block bg-rule ${track}`}>
      {stats.map((stat, i) => (
        <div
          key={stat.label}
          data-role="stat"
          // The reference's `padding:8px 10px`, and 10px across for the reason
          // `GridCell` records: a quote card's figures do not wrap, so it is
          // the one shape that cannot give the mark's gutter back out of its
          // own width.
          // THE LAST CELL FILLS THE ROW IT IS ALONE IN. Seven stats across
          // two columns leaves the seventh beside a hole, and a hole in a
          // ruled grid reads as a figure that failed to load rather than as a
          // row that ran out. Spanning is the honest shape: there is no
          // eighth fact, so nothing is missing.
          //
          // TWO COLUMNS ONLY, deliberately. The three-column track is
          // responsive -- two below 360px and three above it -- so the row a
          // cell is last in depends on a breakpoint this cannot see, and a
          // static span would fill a hole at one width and cut a row short at
          // the other. `columns={2}` is fixed at every width, which is what
          // makes the arithmetic true.
          className={`min-w-0 bg-surface px-2 py-2${
            columns === 2 && i === stats.length - 1 && stats.length % 2 === 1 ? ' col-span-2' : ''
          }`}
        >
          <Label className="block truncate">{humanLabel(stat.label, lang)}</Label>
          <Wide className="mt-0">
            {/* label-m (13px) and mono, which is the reference's own value type. It
                was 14px in the sans the rest of the card uses, and a figure
                set at the same size as a name is a figure that has to be read
                rather than scanned. */}
            <Fig cell={stat.value} className="font-mono text-label-m font-semibold text-ink" />
          </Wide>
        </div>
      ))}
    </div>
  )
}

/**
 * One line of a list: what it is, what you hold, what it is worth, what it
 * did.
 *
 * TWO LINES AT THE FLOOR, AND THAT IS A MEASUREMENT RATHER THAN A
 * PREFERENCE. The reference sets these four across one line with a 62px
 * symbol column and an 82px P/L column: 174px of fixed width plus a ~74px
 * context and a ~62px figure is 310px, against the 262px a 320px panel leaves
 * inside a card. Stacked, the left column keeps about 120px and the right
 * column never has to give a figure up -- which is the priority this whole
 * surface is built on, since a figure that wraps or truncates is worse than a
 * name that does.
 *
 * It is also the shape embed.css opens by naming: label left, figure right,
 * ruled between. The rule comes from `Card`'s divider, so nothing here draws
 * a border and two Rows can never disagree about one.
 *
 * The fixed symbol column goes with it. It clips HIDCLBSL, and a clipped
 * identifier is not a shortened one -- it is a different identifier.
 *
 * Explicit `col-start`/`row-start` rather than source order, because
 * auto-placement drops a delta into the context's cell on a row that carries
 * a delta and no context.
 *
 * `whole` IS FOR THE ROW THAT HAS NOWHERE ELSE TO SAY IT. Truncating is right
 * where the row is one line of a list a reader scans down and the figure on
 * the right is what they came for -- a name is context there, and the whole
 * of it is a column away. It is wrong where the row IS the answer: a ledger
 * record draws a row because its columns did not fit, so there is no column
 * away for the rest of the name to be, and "Nepal Doorsanchar Compa..." is
 * the very defect that shape exists to end. `EntityHead` already argues this
 * about its badge -- "a five-character symbol clipped to four is a different
 * symbol" -- and it is truer of the identifier than of the mark standing for
 * it.
 */
export function Row({
  identifier,
  context,
  figure,
  delta,
  tone = 'symbol',
  whole = false,
  className = '',
  ...rest
}: React.ComponentPropsWithoutRef<'div'> & {
  identifier: React.ReactNode
  context?: React.ReactNode
  figure?: React.ReactNode
  delta?: React.ReactNode
  tone?: 'symbol' | 'label'
  /** Wrap the identifier and its context rather than clipping either. */
  whole?: boolean
}) {
  const lead = tone === 'symbol' ? SYMBOL : 'text-body-s text-ink'
  // `break-words` and not `wrap`: an identifier can be one unbroken token
  // longer than the panel -- a scrip code, a row key -- and a break
  // opportunity it does not have is the difference between wrapping and
  // overflowing the card.
  const fit = whole ? 'break-words' : 'truncate'
  return (
    <div
      data-role="row"
      {...rest}
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3
                  gap-y-0 px-3 py-2 ${className}`}
    >
      <span className={`col-start-1 row-start-1 min-w-0 ${fit} ${lead}`}>{identifier}</span>
      {figure && (
        <span className="col-start-2 row-start-1 justify-self-end text-body-s text-ink">
          {figure}
        </span>
      )}
      {context && (
        <span className={`col-start-1 row-start-2 min-w-0 ${fit} text-label-s text-ink-soft`}>
          {context}
        </span>
      )}
      {delta && <span className="col-start-2 row-start-2 justify-self-end">{delta}</span>}
    </div>
  )
}

/**
 * How old the figures are, how many are on screen, and the way to the rest --
 * on one line, because a reader parses one.
 *
 * These were three separate elements stacked under the shape. They are three
 * facts about the same rows, and the middle is not the same fact as the last:
 * `note` is the ENGINE's cut, the rows a tool matched and did not return,
 * which no control here can open. `action` is the CLIENT's bound.
 *
 * NO DOT. The reference marks the state with a green or amber circle, and
 * Stamp.tsx refuses precisely that: eight states are eight colours to learn,
 * and two of them are indistinguishable to a reader who cannot separate the
 * hues. Its amber is `--flag: #8A5A00`, which is not a token in this theme at
 * all, so drawing it would also be a raw hex on a data state.
 *
 * A STAMP IS NEVER OPTIONAL WHERE THERE IS A FIGURE TO STAMP, and that
 * qualifier is new. This surface is reached most often when the data is
 * degraded, and a four-month-old price that looked current is why the states
 * exist -- so no card that draws a figure may omit one. But the pill is a
 * claim ABOUT a figure, and a card with none was asserting the freshness of
 * something it never drew: a stated absence under AT FRI CLOSE, and a stack
 * of a company's own notices under the same. `Projection` owns that judgement
 * because only it can see what the body drew; `null` here is how it says so,
 * and the whole strip goes when the stamp was the only thing in it.
 */
export function Foot({
  state,
  asOf,
  note,
  action,
  destination: target,
}: {
  /** Null where the card drew no figure for a stamp to qualify. */
  state: DataState | null
  asOf: string
  /** The engine's own cut, already substituted from `words.showingOf`. */
  note?: string | null
  /** The client's bound, usually the `showAll` control. */
  action?: React.ReactNode
  /** Where these rows live in the platform. See surface/destinations.py. */
  destination?: { path: string; label: string } | null
}) {
  // A pill to a path this app has no route for opens PageNotFound, so it is
  // not drawn (staging, 2026-11-01); the router's own matching decides.
  const routable = useRoutable()
  const destination = target && routable(target.path) ? target : null
  // A ruled, padded strip with nothing in it is the empty grid `Table` already
  // refuses one screen up, reached from the other end: the stamp was the only
  // occupant of most feet, so dropping it has to drop the strip or the card
  // grows a hairline under a sentence and stops there.
  if (!state && !note && !action && !destination) return null
  return (
    <div data-role="foot" className="flex flex-wrap items-center gap-x-2 gap-y-2 px-3 py-2">
      {/* THE STAMP IS A PILL AND THE STRIP IS GONE. A full-width `bg-ground`
          bar under every card made the freshness the heaviest object on the
          surface -- it is a qualifier, not a conclusion. The words are
          `stampText`'s own and unchanged, so the register story is untouched
          and `Stamp.test.tsx` still holds; what changed is that a qualifier
          now looks like one. Same device as the prototype, which sets its
          session line in a soft pill beside the price. */}
      {state && (
        <span
          data-role="stamp-pill"
          className="inline-flex items-center rounded-pill bg-ground px-2 py-1"
        >
          <Stamp state={state} asOf={asOf} />
        </span>
      )}
      {note && <span className="text-label-s text-ink-soft">{note}</span>}
      {action && <span className="ml-auto">{action}</span>}
      {destination && (
        <span className={action ? '' : 'ml-auto'}>
          <Destination path={destination.path} label={destination.label} />
        </span>
      )}
    </div>
  )
}

/**
 * The control that hands the reader back to the platform.
 *
 * IN THE FOOT AND NOT IN THE HEADER, which is a decision about what this is.
 * A destination is not a call to action — the engine describes and does not
 * prescribe, and a prominent control under a figure reads as "do this". It
 * belongs beside the stamp, in the register of "this is where these rows
 * live", which is what it says.
 *
 * Opened through the app's router (`useOpenPath`), in this tab, so the
 * conversation's state is kept; only a host-relative path is followed, and
 * `Foot` draws it only where the router has a page for it (`useRoutable`).
 *
 * The label is the platform's own page name, in English, for the reason
 * `surface/destinations.py` gives: a translated label pointing at an English
 * page names something the reader will not find when they arrive.
 */
function Destination({ path, label }: { path: string; label: string }) {
  const openPath = useOpenPath()
  return (
    <button
      type="button"
      data-role="destination"
      data-path={path}
      onClick={() => {
        openPath(path)
      }}
      // Figma-adjacent 10px -> px-3 (4-grid; hit-area rule rounds a clickable control up)
      className="inline-flex items-center gap-1 rounded-pill border border-edge
                 px-3 py-1 text-mono-s text-ink-mid
                 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {label}
      <span aria-hidden="true">↗</span>
    </button>
  )
}
